import {PageContainer, PageTitle} from '@hconnect/uikit/src/lib2'
import {Box} from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import {KilnProvider, SensorsProvider} from '@widgets/kms'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {Outlet, useSearchParams} from 'react-router'

import {useUrlParam} from '../../../shared/hooks/useUrlParam'
import {ControlPanel} from '../../general/components/ControlPanel'
import {SignalCharts} from '../../general/components/SignalCharts'
import {
  usePlantMonitoring,
  usePlantMonitoringSelectors
} from '../../general/context/PlantMonitoringContext'
import {SubscriptionButton} from '../../shared/components/SubscriptionButton'
import {usePlantUpmId} from '../../shared/hooks/usePlantUpmId'
import {ModelWrapper} from '../containers/ModelWrapper'

export const KMS = () => {
  const {t} = useTranslation()
  const {state} = usePlantMonitoring()

  const signalTreeGridConfig = state.uiLayout.signalTreeCollapsed
    ? ({
        xs: 12,
        md: 12,
        lg: 'auto'
      } as const)
    : ({
        xs: 12,
        md: 12,
        lg: 5,
        xl: 4
      } as const)

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        gap={1}
        alignItems={{xs: 'stretch', md: 'flex-start'}}
        flexDirection={{xs: 'column', sm: 'row'}}
      >
        <PageTitle
          mb={0}
          pt={0.5}
          data-test-id="kms-page-title"
          maxWidth="fit-content"
          display={{xs: 'none', md: 'block'}}
        >
          {t('general.kmsTitle')}
        </PageTitle>
        <SubscriptionButton />
        <ControlPanel />
      </Box>
      <Grid container spacing={2} mt={2}>
        <Grid {...signalTreeGridConfig} component="div" id="signal-tree"></Grid>
        <Grid xs={true}>
          <ModelWrapper />
          <SignalCharts
            disableGroupEdit
            CardBoxProps={{
              boxShadow: 0,
              style: {borderTopLeftRadius: 0, borderTopRightRadius: 0}
            }}
          />
        </Grid>
      </Grid>
    </>
  )
}

export const KmsContainer = () => {
  const kilnId = useUrlParam('kilnId')
  const plantUpmId = usePlantUpmId()
  const {selectedIds} = usePlantMonitoringSelectors()
  const [, setSearchParams] = useSearchParams()

  const toggleSensorSelected = useCallback(
    (sensorId: string) => {
      setSearchParams((params) => {
        const selected = new Set(params.get('selected')?.split(','))
        if (selected.has(sensorId)) {
          selected.delete(sensorId)
        } else {
          selected.add(sensorId)
        }
        if (selected.size === 0) {
          params.delete('selected')
        } else {
          params.set('selected', Array.from(selected).join(','))
        }
        return params
      })
    },
    [setSearchParams]
  )

  return (
    <PageContainer display="flex" flexDirection="column" bgcolor="#01407F" flexGrow={1}>
      <KilnProvider kilnId={kilnId} plantUpmId={plantUpmId}>
        <SensorsProvider
          kilnId={kilnId}
          plantUpmId={plantUpmId}
          selectedIds={selectedIds}
          onToggleSensorSelected={toggleSensorSelected}
        >
          <Outlet />
        </SensorsProvider>
      </KilnProvider>
    </PageContainer>
  )
}
